/**
 * Help Center Chart Registry
 *
 * Type-safe registry for interactive charts used in help articles.
 * Built on @shopify/polaris-viz for consistent styling.
 */

import { lazy, ComponentType } from 'react';

/** Props passed to all chart components */
export interface ChartProps {
  /** Optional CSS class name */
  className?: string;
  /** Chart height in pixels */
  height?: number;
  /** Whether to show legend */
  showLegend?: boolean;
  /** Whether chart is interactive */
  interactive?: boolean;
}

/** Available chart identifiers */
export type ChartName =
  | 'VelocityExampleChart'
  | 'AlertDistributionChart'
  | 'SeasonalTrendsChart'
  | 'StockoutPredictionChart'
  | 'LookbackComparisonChart';

/** Chart metadata */
export interface ChartMeta {
  /** Human-readable title */
  title: string;
  /** Brief description */
  description: string;
  /** Chart type for styling hints */
  type: 'line' | 'bar' | 'donut' | 'area' | 'stacked';
  /** Recommended height */
  defaultHeight: number;
  /** Whether this chart has interactive features */
  interactive: boolean;
}

/** Registry of chart metadata */
export const chartMeta: Record<ChartName, ChartMeta> = {
  VelocityExampleChart: {
    title: 'Sales Velocity Over Time',
    description: 'Example of daily sales velocity calculation',
    type: 'line',
    defaultHeight: 280,
    interactive: true,
  },
  AlertDistributionChart: {
    title: 'Alert Distribution by Severity',
    description: 'Breakdown of alerts by warning, urgent, and critical levels',
    type: 'donut',
    defaultHeight: 240,
    interactive: true,
  },
  SeasonalTrendsChart: {
    title: 'Seasonal Sales Pattern',
    description: 'Monthly seasonal multipliers throughout the year',
    type: 'bar',
    defaultHeight: 280,
    interactive: true,
  },
  StockoutPredictionChart: {
    title: 'Stockout Prediction Timeline',
    description: 'Inventory depletion over time with prediction line',
    type: 'area',
    defaultHeight: 280,
    interactive: true,
  },
  LookbackComparisonChart: {
    title: 'Lookback Period Impact',
    description: 'How different lookback periods affect velocity calculation',
    type: 'line',
    defaultHeight: 320,
    interactive: true,
  },
};

/** Lazy-loaded chart components */
export const charts: Record<ChartName, ComponentType<ChartProps>> = {
  VelocityExampleChart: lazy(() => import('./VelocityExampleChart')),
  AlertDistributionChart: lazy(() => import('./AlertDistributionChart')),
  SeasonalTrendsChart: lazy(() => import('./SeasonalTrendsChart')),
  StockoutPredictionChart: lazy(() => import('./StockoutPredictionChart')),
  LookbackComparisonChart: lazy(() => import('./LookbackComparisonChart')),
};

/** Type guard to check if a string is a valid chart name */
export function isChartName(name: string): name is ChartName {
  return name in charts;
}

/** Get chart component by name (returns undefined if not found) */
export function getChart(name: string): ComponentType<ChartProps> | undefined {
  if (isChartName(name)) {
    return charts[name];
  }
  return undefined;
}

/** Get chart metadata by name */
export function getChartMeta(name: string): ChartMeta | undefined {
  if (isChartName(name)) {
    return chartMeta[name];
  }
  return undefined;
}
